import { useEffect, useState } from 'react';
import { SearchModeToggle } from './SearchModeToggle';

interface IndexBoost {
  indexName: string;
  displayName: string;
  boost: number;
  enabled: boolean;
}

interface RecordBoostingConfig {
  defaultBoost: number;
  defaultBoostResearch?: number;
  indices: IndexBoost[];
  indicesResearch?: IndexBoost[];
  lastUpdated: string;
}

interface RecordBoostingProps {
  onSave?: (config: RecordBoostingConfig) => Promise<void>;
  onReset?: () => void;
  saving?: boolean;
}

interface SearchModeConfig {
  mode: 'regular' | 'research';
  name: string;
  description: string;
  enabledIndexes: string[];
  lastUpdated: string;
}

interface AllSearchModesConfig {
  regular: SearchModeConfig;
  research: SearchModeConfig;
}

const AVAILABLE_INDICES = [
  // ASM Info and Products (reordered to match search bar)
  { name: 'asmi-wp-pages', displayName: 'Member & Society Info' },
  { name: 'asmi-wp-products,asmi-marketing-gear-store', displayName: 'Products' },
  { name: 'asmi-wp-events', displayName: 'Education Courses' },
  { name: 'asmi-pages-events', displayName: 'Event Pages' },
  { name: 'asmi-wp-asm-news', displayName: 'Industry News' },
  { name: 'asmi-wp-videos', displayName: 'Videos' },
  // Digital Book Content (consolidated group)
  { name: 'asmi-dl-books', displayName: 'Digital Books' },
  { name: 'asmi-dl-chapters', displayName: 'Digital Book Chapters' },
  { name: 'asmi-dl-chapter-images', displayName: 'Digital Book Images' },
  { name: 'asmi-dl-chapter-tables', displayName: 'Digital Book Tables' },
  // Other Digital Library Content
  { name: 'asmi-dl-technical_articles', displayName: 'Magazine Articles' },
  { name: 'asmi-wp-news_articles', displayName: 'ASM Member News' },
  { name: 'asmi-dl-proceedings', displayName: 'Conference Papers' },
  { name: 'asmi-dl-datasheets', displayName: 'Alloy Digest Datasheets' },
  // Materials Data indexes
  { name: 'asmi-mat-matdb-materials', displayName: 'Materials Database' },
  { name: 'asmi-mat-alloyfinder-alloy-finder', displayName: 'Alloy Finder' },
  { name: 'asmi-mat-datasheets-data-sheets-and-diagrams', displayName: 'Materials Datasheets' },
  { name: 'asmi-mat-grantami_mgd-micrograph-data', displayName: 'Micrograph Data' },
  { name: 'asmi-mat-corrosion-uniform-corrosion-data', displayName: 'Corrosion Data' },
  { name: 'asmi-mat-corrosion-environments', displayName: 'Corrosion Environments' },
  { name: 'asmi-mat-matdb-documents', displayName: 'Materials Documents' },
  { name: 'asmi-mat-datasheets-data-sheet-sources', displayName: 'Materials Data Sources' },
  { name: 'asmi-mat-matdb-reference', displayName: 'Materials References' },
  { name: 'asmi-mat-corrosion-design-data', displayName: 'Corrosion Design Data' },
  { name: 'asmi-mat-corrosion-references', displayName: 'Corrosion References' },
  { name: 'asmi-mat-alloyfinder-organizations', displayName: 'Alloy Organizations' },
  { name: 'asmi-mat-corrosion-materials', displayName: 'Corrosion Materials' },
  { name: 'asmi-mat-corrosion-high-temperature-oxidation-data', displayName: 'High-Temperature Oxidation Data' },
  { name: 'asmi-mat-alloy-phase-diagrams', displayName: 'Phase Diagrams' },
  { name: 'asmi-mat-alloy-phase-structures', displayName: 'Crystallographic Structures' },
  { name: 'asmi-mat-medical-materials', displayName: 'Medical Materials Database' },
];

export function RecordBoosting({ onSave, onReset, saving = false }: RecordBoostingProps) {
  const [config, setConfig] = useState<RecordBoostingConfig>({
    defaultBoost: 1.0,
    indices: AVAILABLE_INDICES.map((idx) => ({
      indexName: idx.name,
      displayName: idx.displayName,
      boost: idx.name === 'asmi-dl-datasheets' ? 2.0 : 1.0, // Default: boost datasheets
      enabled: true,
    })),
    lastUpdated: new Date().toISOString(),
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentMode, setCurrentMode] = useState<'regular' | 'research'>('regular');
  const [showJsonImport, setShowJsonImport] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [searchModesConfig, setSearchModesConfig] = useState<AllSearchModesConfig | null>(null);

  const API_BASE_URL = import.meta.env.VITE_API_URL || '';

  useEffect(() => {
    fetchConfig();
  }, []);

  // Fetch search modes configuration
  useEffect(() => {
    const fetchSearchModes = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/search-modes`);
        if (response.ok) {
          const data = await response.json();
          setSearchModesConfig(data);
        }
      } catch (error) {
        console.error('Failed to fetch search modes:', error);
      }
    };
    fetchSearchModes();
  }, [API_BASE_URL]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/record-boosting`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Failed to fetch record boosting config:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize research mode data if it doesn't exist
  useEffect(() => {
    if (currentMode === 'research') {
      if (!config.indicesResearch) {
        setConfig((prev) => ({
          ...prev,
          indicesResearch: JSON.parse(JSON.stringify(prev.indices)),
        }));
      }
      if (config.defaultBoostResearch === undefined) {
        setConfig((prev) => ({
          ...prev,
          defaultBoostResearch: prev.defaultBoost,
        }));
      }
    }
  }, [currentMode, config.indicesResearch, config.defaultBoostResearch]);

  // Get current indices based on mode, filtered by search mode enabled indexes
  const getCurrentIndices = (): IndexBoost[] => {
    let indices: IndexBoost[];
    if (currentMode === 'research' && config.indicesResearch) {
      indices = config.indicesResearch;
    } else {
      indices = config.indices;
    }

    // Filter by search mode enabled indexes if config is available
    if (searchModesConfig) {
      const enabledIndexes = searchModesConfig[currentMode].enabledIndexes;
      return indices.filter((idx) => enabledIndexes.includes(idx.indexName));
    }

    return indices;
  };

  // Get current default boost based on mode
  const getCurrentDefaultBoost = (): number => {
    if (currentMode === 'research' && config.defaultBoostResearch !== undefined) {
      return config.defaultBoostResearch;
    }
    return config.defaultBoost;
  };

  const handleBoostChange = (indexName: string, newBoost: number) => {
    setConfig((prev) => {
      if (currentMode === 'research') {
        return {
          ...prev,
          indicesResearch: (prev.indicesResearch || prev.indices).map((idx) =>
            idx.indexName === indexName ? { ...idx, boost: newBoost } : idx
          ),
          lastUpdated: new Date().toISOString(),
        };
      } else {
        return {
          ...prev,
          indices: prev.indices.map((idx) => (idx.indexName === indexName ? { ...idx, boost: newBoost } : idx)),
          lastUpdated: new Date().toISOString(),
        };
      }
    });
    setHasChanges(true);
  };

  const handleEnabledChange = (indexName: string, enabled: boolean) => {
    setConfig((prev) => {
      if (currentMode === 'research') {
        return {
          ...prev,
          indicesResearch: (prev.indicesResearch || prev.indices).map((idx) =>
            idx.indexName === indexName ? { ...idx, enabled } : idx
          ),
          lastUpdated: new Date().toISOString(),
        };
      } else {
        return {
          ...prev,
          indices: prev.indices.map((idx) => (idx.indexName === indexName ? { ...idx, enabled } : idx)),
          lastUpdated: new Date().toISOString(),
        };
      }
    });
    setHasChanges(true);
  };

  const handleDefaultBoostChange = (newDefaultBoost: number) => {
    setConfig((prev) => {
      if (currentMode === 'research') {
        return {
          ...prev,
          defaultBoostResearch: newDefaultBoost,
          lastUpdated: new Date().toISOString(),
        };
      } else {
        return {
          ...prev,
          defaultBoost: newDefaultBoost,
          lastUpdated: new Date().toISOString(),
        };
      }
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave(config);
      setHasChanges(false);
    }
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
      setHasChanges(false);
    }
  };

  const handleExportJson = () => {
    // Export ONLY the current mode's data
    const exportData = {
      defaultBoost: getCurrentDefaultBoost(),
      indices: getCurrentIndices(),
      lastUpdated: config.lastUpdated || new Date().toISOString(),
    };
    const jsonStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `record-boosting-${currentMode}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportJson = () => {
    try {
      setJsonError('');
      const parsed = JSON.parse(jsonInput);

      // Validate the structure
      if (!parsed.indices || !Array.isArray(parsed.indices)) {
        throw new Error('Invalid JSON: missing "indices" array');
      }

      // Validate each index has required fields
      for (const idx of parsed.indices) {
        if (!idx.indexName || idx.boost === undefined) {
          throw new Error('Invalid JSON: each index must have "indexName" and "boost" properties');
        }

        // Validate boost values are within acceptable range (0.1 to 3)
        if (typeof idx.boost === 'number') {
          if (idx.boost > 3) {
            throw new Error(
              `Invalid boost: index "${idx.indexName || idx.displayName}" has boost ${idx.boost} (maximum is 3)`
            );
          }
          if (idx.boost < 0.1) {
            throw new Error(
              `Invalid boost: index "${idx.indexName || idx.displayName}" has boost ${idx.boost} (minimum is 0.1)`
            );
          }
        }
      }

      // Validate default boost if present
      if (parsed.defaultBoost !== undefined && typeof parsed.defaultBoost === 'number') {
        if (parsed.defaultBoost > 3) {
          throw new Error(`Invalid default boost: ${parsed.defaultBoost} (maximum is 3)`);
        }
        if (parsed.defaultBoost < 0.1) {
          throw new Error(`Invalid default boost: ${parsed.defaultBoost} (minimum is 0.1)`);
        }
      }

      // Update ONLY the current mode's data, not both modes
      if (currentMode === 'research') {
        setConfig({
          ...config,
          indicesResearch: parsed.indices,
          defaultBoostResearch: parsed.defaultBoost || config.defaultBoostResearch || config.defaultBoost,
          lastUpdated: new Date().toISOString(),
        });
      } else {
        setConfig({
          ...config,
          indices: parsed.indices,
          defaultBoost: parsed.defaultBoost || config.defaultBoost,
          lastUpdated: new Date().toISOString(),
        });
      }
      setHasChanges(true);
      setShowJsonImport(false);
      setJsonInput('');
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON format');
    }
  };

  const getBoostLabel = (boost: number) => {
    if (boost < 0.5) return 'Very Low';
    if (boost < 1.0) return 'Low';
    if (boost === 1.0) return 'Baseline';
    if (boost <= 1.5) return 'High';
    if (boost <= 2.0) return 'Very High';
    return 'Maximum';
  };

  const getBoostColor = (boost: number) => {
    if (boost < 0.5) return '#ff4444';
    if (boost < 1.0) return '#ff8800';
    if (boost === 1.0) return '#888888';
    if (boost <= 1.5) return '#44aa44';
    if (boost <= 2.0) return '#2266dd';
    return '#8844dd';
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        <p className="mt-4 text-zinc-600">Loading record boosting configuration...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          background: var(--boost-color, #18181b);
        }
        input[type="range"]::-moz-range-thumb {
          background: var(--boost-color, #18181b);
        }
      `}</style>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-zinc-900 mb-2">Record Boosting Configuration</h2>
          <p className="text-sm text-zinc-600">
            Control how different content types are prioritized in search results. Higher boost values make content
            appear higher in search rankings.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            <button
              onClick={handleExportJson}
              className="px-3 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-lg hover:bg-zinc-50 transition-colors flex items-center gap-2"
              title="Export configuration as JSON"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Export
            </button>
            <button
              onClick={() => setShowJsonImport(true)}
              className="px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              title="Import configuration from JSON"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              Import
            </button>
          </div>
          <SearchModeToggle currentMode={currentMode} onModeChange={setCurrentMode} />
        </div>
      </div>

      {/* JSON Import Modal */}
      {showJsonImport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-zinc-200">
              <div>
                <h3 className="text-lg font-semibold text-zinc-900">Import JSON Configuration</h3>
                <p className="text-sm text-zinc-600 mt-1">Paste your JSON configuration below</p>
              </div>
              <button
                onClick={() => {
                  setShowJsonImport(false);
                  setJsonInput('');
                  setJsonError('');
                }}
                className="text-zinc-400 hover:text-zinc-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <textarea
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder={`{\n  "defaultBoost": 1,\n  "indices": [\n    {\n      "indexName": "asmi-wp-events",\n      "displayName": "Education Courses",\n      "boost": 8.0,\n      "enabled": true\n    }\n  ]\n}`}
                className="w-full h-96 p-4 font-mono text-sm border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
              {jsonError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">Error:</p>
                  <p className="text-sm text-red-700 mt-1">{jsonError}</p>
                </div>
              )}
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Quick Tip</h4>
                <p className="text-sm text-blue-800 mb-2">
                  You can use the exported JSON from{' '}
                  <code className="bg-blue-100 px-1 rounded">optimized-record-boosting-config.json</code>
                </p>
                <p className="text-sm text-blue-800">
                  The JSON must have an <code className="bg-blue-100 px-1 rounded">indices</code> array with objects
                  containing <code className="bg-blue-100 px-1 rounded">indexName</code> and{' '}
                  <code className="bg-blue-100 px-1 rounded">boost</code> fields.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-zinc-200">
              <button
                onClick={() => {
                  setShowJsonImport(false);
                  setJsonInput('');
                  setJsonError('');
                }}
                className="px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-lg hover:bg-zinc-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImportJson}
                disabled={!jsonInput.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Import Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Explanation Section */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-base font-semibold text-blue-900 mb-4">How Record Boosting Works</h3>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Purpose</h4>
            <p className="text-sm text-zinc-700 leading-relaxed">
              Record boosting multiplies the relevance score of documents from specific indexes, making them appear
              higher in search results.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">How It's Applied</h4>
            <p className="text-sm text-zinc-700 leading-relaxed mb-2">
              <strong>Final Score = (Field Weight × Query Match) × Record Boost</strong>
            </p>
            <p className="text-sm text-zinc-700 leading-relaxed">
              Record boosting works alongside field weighting to create sophisticated relevance ranking.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Example</h4>
            <ul className="space-y-1">
              <li className="bg-white/70 rounded px-3 py-1.5 text-xs font-mono text-zinc-800">
                Chapter title match: 3 × 0.8 × 1.0 = 2.4
              </li>
              <li className="bg-white/70 rounded px-3 py-1.5 text-xs font-mono text-zinc-800">
                Datasheet keyword match: 2 × 0.6 × 2.0 = 2.4
              </li>
              <li className="bg-white/70 rounded px-3 py-1.5 text-xs font-mono text-zinc-800">
                Book content match: 1 × 0.9 × 1.5 = 1.35
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Default Boost Section */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6">
        <h3 className="text-base font-semibold text-zinc-900 mb-1">Default Boost Value</h3>
        <p className="text-sm text-zinc-600 mb-4">Baseline boost applied to indexes not explicitly configured</p>
        <div className="flex items-center gap-5">
          <input
            type="range"
            min="0.1"
            max="3.0"
            step="0.1"
            value={getCurrentDefaultBoost()}
            onChange={(e) => handleDefaultBoostChange(parseFloat(e.target.value))}
            className="flex-1 h-2 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0"
            style={
              {
                background: `linear-gradient(to right, ${getBoostColor(getCurrentDefaultBoost())} 0%, ${getBoostColor(getCurrentDefaultBoost())} ${((getCurrentDefaultBoost() - 0.1) / 2.9) * 100}%, #e4e4e7 ${((getCurrentDefaultBoost() - 0.1) / 2.9) * 100}%, #e4e4e7 100%)`,
                ['--boost-color' as string]: getBoostColor(getCurrentDefaultBoost()),
              } as React.CSSProperties
            }
          />
          <div className="flex flex-col items-center min-w-[80px]">
            <span className="text-lg font-semibold text-zinc-900">{getCurrentDefaultBoost().toFixed(1)}</span>
            <span
              className="text-xs font-medium uppercase tracking-wide"
              style={{ color: getBoostColor(getCurrentDefaultBoost()) }}
            >
              {getBoostLabel(getCurrentDefaultBoost())}
            </span>
          </div>
        </div>
      </div>

      {/* Index-Specific Boost Controls */}
      <div>
        <h3 className="text-base font-semibold text-zinc-900 mb-4">Index-Specific Boost Values</h3>
        <div className="space-y-4">
          {getCurrentIndices().map((indexBoost) => (
            <div
              key={indexBoost.indexName}
              className={`border rounded-lg p-5 transition-all cursor-pointer ${
                indexBoost.enabled
                  ? 'bg-white border-zinc-200 hover:shadow-md'
                  : 'bg-zinc-50 border-zinc-100 opacity-70'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-base font-semibold text-zinc-900 mb-1">{indexBoost.displayName}</h4>
                  <code className="text-xs font-mono bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded">
                    {indexBoost.indexName}
                  </code>
                </div>
                <div className="flex items-center gap-2">
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={indexBoost.enabled}
                      onChange={(e) => handleEnabledChange(indexBoost.indexName, e.target.checked)}
                      className="peer sr-only"
                    />
                    <div className="peer h-6 w-11 rounded-full bg-zinc-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-zinc-900 peer-checked:after:translate-x-5 peer-focus:ring-2 peer-focus:ring-zinc-900 peer-focus:ring-offset-2"></div>
                  </label>
                  <span className="text-sm font-medium text-zinc-700">
                    {indexBoost.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-5">
                <div className="flex-1">
                  <input
                    type="range"
                    min="0.1"
                    max="3.0"
                    step="0.1"
                    value={indexBoost.boost}
                    onChange={(e) => handleBoostChange(indexBoost.indexName, parseFloat(e.target.value))}
                    disabled={!indexBoost.enabled}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110 disabled:[&::-webkit-slider-thumb]:cursor-not-allowed [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:shadow-md"
                    style={
                      {
                        background: indexBoost.enabled
                          ? `linear-gradient(to right, ${getBoostColor(indexBoost.boost)} 0%, ${getBoostColor(indexBoost.boost)} ${((indexBoost.boost - 0.1) / 2.9) * 100}%, #e4e4e7 ${((indexBoost.boost - 0.1) / 2.9) * 100}%, #e4e4e7 100%)`
                          : '#e4e4e7',
                        ['--boost-color' as string]: indexBoost.enabled ? getBoostColor(indexBoost.boost) : '#a1a1aa',
                      } as React.CSSProperties
                    }
                  />
                </div>
                <div className="flex flex-col items-center min-w-[80px]">
                  <span className={`text-lg font-semibold ${indexBoost.enabled ? 'text-zinc-900' : 'text-zinc-400'}`}>
                    {indexBoost.boost.toFixed(1)}
                  </span>
                  <span
                    className="text-xs font-medium uppercase tracking-wide"
                    style={{ color: getBoostColor(indexBoost.boost) }}
                  >
                    {getBoostLabel(indexBoost.boost)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Boost Impact Preview */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6">
        <h3 className="text-base font-semibold text-zinc-900 mb-4">Boost Impact Preview</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {getCurrentIndices()
            .filter((idx) => idx.enabled)
            .sort((a, b) => b.boost - a.boost)
            .slice(0, 5)
            .map((indexBoost, index) => (
              <div
                key={indexBoost.indexName}
                className="flex items-center gap-3 bg-white border border-zinc-200 rounded-lg p-3"
              >
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white flex-shrink-0">
                  #{index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-900 truncate">{indexBoost.displayName}</div>
                  <div className="text-xs text-zinc-600">Boost: {indexBoost.boost.toFixed(1)}x</div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3 pt-4 border-t border-zinc-200">
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-all hover:shadow-md"
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
        <button
          type="button"
          onClick={handleReset}
          disabled={saving}
          className="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
        >
          Reset to Defaults
        </button>
      </div>

      {/* Last Updated */}
      {config.lastUpdated && (
        <div className="text-sm text-zinc-500 text-center">
          Last updated: {new Date(config.lastUpdated).toLocaleString()}
        </div>
      )}
    </div>
  );
}

export default RecordBoosting;
